import { useState, useEffect } from 'react';

interface GasInfo {
  gasPrice: string;
  maxFeePerGas: string;
  maxPriorityFeePerGas: string;
  estimatedTransactionCost: string;
  isLoading: boolean;
  error: string | null;
}

export function useGasPrice(): GasInfo {
  const [gasInfo, setGasInfo] = useState<GasInfo>({
    gasPrice: '0',
    maxFeePerGas: '0',
    maxPriorityFeePerGas: '0',
    estimatedTransactionCost: '0',
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const fetchGasPrice = async () => {
      try {
        setGasInfo(prev => ({ ...prev, isLoading: true, error: null }));

        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        // Use API endpoint instead of direct RPC access
        const response = await fetch('/api/gas-price?network=mainnet', {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache',
          }
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch gas price from API`);
        }

        const result = await response.json();
        if (!result.success) {
          throw new Error(result.error || 'API returned error');
        }

        const data = result.data;
        
        console.log('[GasPrice] Debug info:', {
          gasPrice: data.gasPrice,
          maxFeePerGas: data.maxFeePerGas,
          effectiveGasPrice: data.effectiveGasPrice,
          estimatedGasLimit: data.estimatedGasLimit,
          estimatedCostInEth: data.estimatedTransactionCost
        });

        setGasInfo({
          gasPrice: data.gasPrice,
          maxFeePerGas: data.maxFeePerGas,
          maxPriorityFeePerGas: data.maxPriorityFeePerGas,
          estimatedTransactionCost: data.estimatedTransactionCost,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Error fetching gas price:', error);
        
        // Don't show error if it's just a timeout, but show error for API failures
        const isTimeout = error instanceof Error && error.name === 'AbortError';
        const isNetworkError = error instanceof Error && error.message.includes('Failed to fetch');
        const isApiError = error instanceof Error && error.message.includes('HTTP 500');
        
        setGasInfo(prev => ({
          ...prev,
          isLoading: false,
          error: (isTimeout || (isNetworkError && !isApiError)) ? null : (error instanceof Error ? error.message : 'Failed to fetch gas price'),
        }));
      }
    };

    fetchGasPrice();

    // Update every 2 minutes to reduce load and prevent webpack issues
    const interval = setInterval(fetchGasPrice, 120000);

    return () => clearInterval(interval);
  }, []);

  return gasInfo;
}

// Function to determine gas status
export function getGasStatus(gasPrice: string): {
  status: 'low' | 'medium' | 'high' | 'very-high';
  label: string;
  color: string;
  bgColor: string;
} {
  const price = parseFloat(gasPrice);
  
  if (price < 10) {
    return { 
      status: 'low', 
      label: 'Low', 
      color: 'text-green-600',
      bgColor: 'bg-green-500'
    };
  } else if (price < 30) {
    return { 
      status: 'medium', 
      label: 'Medium', 
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-500'
    };
  } else if (price < 100) {
    return { 
      status: 'high', 
      label: 'High', 
      color: 'text-orange-600',
      bgColor: 'bg-orange-500'
    };
  } else {
    return { 
      status: 'very-high', 
      label: 'Very High', 
      color: 'text-red-600',
      bgColor: 'bg-red-500'
    };
  }
} 